import { getScopedDomain } from '../lib/tenantScope';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Wand2, Play, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

// Returns true when the current user has an admin/manager role that grants
// visibility over the whole department voicemail pool.
function isVoicemailAdmin(me: any): boolean {
  if (!me) return false;
  if (me.role === 'super_admin') return true;
  const adminRoles = ['Manager', 'Department Administrator', 'System Admin', 'System Owner'];
  return adminRoles.includes(me.app_role || '');
}

export default function Voicemail() {
  const me = api.me();
  const qc = useQueryClient();
  const scopedDomain = getScopedDomain(me?.domain_uuid);
  // Non-admin users see only their own extension's mailbox.
  const extFilter = isVoicemailAdmin(me) ? undefined : (me?.username as string | undefined);

  const { data: list = [] } = useQuery({
    queryKey: ['vm', scopedDomain, extFilter],
    queryFn: () => scopedDomain ? api.voicemail.list(scopedDomain, extFilter) : Promise.resolve([]),
    enabled: !!scopedDomain,
  });

  // Pull AI metadata too and merge by call_uuid → wav_file/uuid match.
  const { data: aiList = [] } = useQuery({
    queryKey: ['vm-meta', scopedDomain, extFilter],
    queryFn: () => scopedDomain ? api.voicemailMetadata.list({ domain_uuid: scopedDomain, callee: extFilter, limit: 500 }) : Promise.resolve([]),
    enabled: !!scopedDomain,
  });
  // Index AI rows by callee+caller+rough-time so we can attach summary/sentiment
  // even when call_uuid isn't a join key.
  const aiByKey = new Map<string, any>();
  for (const a of aiList as any[]) {
    if (a.wav_file) aiByKey.set(a.wav_file, a);
  }
  const transcribe = useMutation({
    mutationFn: (id: string) => api.voicemail.transcribe(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vm', scopedDomain] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.voicemail.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vm', scopedDomain] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Voicemail / Recordings</h1>
        <Link to="/voicemail-ai" className="btn inline-flex items-center gap-1 text-sm">
          <Sparkles size={14} /> AI view (sentiment + summary)
        </Link>
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th>From</th>
              <th>To</th>
              <th>Date</th>
              <th>Duration</th>
              <th>Sentiment</th>
              <th>Summary</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(list as any[]).map((v: any) => {
              const ai = aiByKey.get(v.wav_file);
              const summary = v.summary || ai?.summary || '';
              const sentiment = v.sentiment || (ai?.sentiment_score != null ? { overall: ai.sentiment_score / 100 } : null);
              return (
              <tr key={v.call_uuid} className="hover:bg-elevated">
                <td>
                  <a href={api.voicemail.playUrl(v.call_uuid)} target="_blank" rel="noreferrer" className="btn-ghost p-1 inline-flex"><Play size={14} /></a>
                </td>
                <td className="font-mono">{v.caller || '—'}</td>
                <td className="font-mono">{v.callee || '—'}</td>
                <td className="text-xs">{v.started_at ? new Date(v.started_at).toLocaleString() : '—'}</td>
                <td>{v.duration_sec ? `${v.duration_sec}s` : '—'}</td>
                <td>{sentiment ? <SentimentBadge s={sentiment} /> : '—'}</td>
                <td className="text-xs max-w-md truncate">{summary}</td>
                <td className="space-x-1 whitespace-nowrap">
                  <button onClick={() => transcribe.mutate(v.call_uuid)} className="btn-ghost p-1" title="Transcribe"><Wand2 size={14} /></button>
                  <button onClick={() => remove.mutate(v.call_uuid)} className="btn-ghost p-1 text-danger" title="Delete"><Trash2 size={14} /></button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SentimentBadge({ s }: { s: any }) {
  const o = s?.overall ?? 0;
  if (o >= 0.3) return <span className="badge-up">positive</span>;
  if (o <= -0.3) return <span className="badge-down">negative</span>;
  return <span className="badge-warn">neutral</span>;
}
